import React, { useState, useEffect, useMemo } from "react";
import {
  Input,
  Row,
  Card,
  Table,
  Spin,
  Button,
  Space,
  Form,
  Select,
  Pagination,
} from "antd";
import { ArrowDownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import DownloadTableButtons from "../../../components/layouts/enhancement/DownloadTableButtons";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import { labelConfig, staticConfig, apiConfig } from "./securityProduct.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import { BOM } from "../../../services/definedMethod";

// ===================== DUMMY DATA =====================

const ManageSecurityProduct = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { userData, rightBtn, rightAction } = props;
  const { session } = userData || {};
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [securityList, setSecurityList] = useState([]);
  const [productTypeFilter, setProductTypeFilter] = useState("ALL");

  const [loading, setLoading] = useState(false);
  const productTypeOptions = [
    { label: "All Products", value: "ALL" },
    { label: "Treasury Bills", value: "TREASURY_BILL" },
    { label: "Bonds", value: "BOND" },
  ];

  const pageSize = 10;

  // ===================== COLUMNS =====================
  const columns = [
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Product Name{" "}
          <ArrowDownOutlined style={{ fontSize: 14, color: "gray" }} />
        </div>
      ),
      dataIndex: "productName",
      sorter: (a, b) =>
        String(a.productName).localeCompare(String(b.productName)),
    },
    {
      title: "Currency Code",
      dataIndex: "currencyCode",
    },
    {
      title: "Product Code",
      dataIndex: "productCode",
    },
    {
      title: "Product Type",
      dataIndex: "productType",
    },
  ];

  useEffect(() => {
    getSecurityInstrumentList();
  }, []);
  // ===================== SEARCH =====================
  const handleSearchInTable = (e) => {
    setSearchTerm(e.target.value || "");
    setCurrentPage(1);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;

    const q = searchTerm.toLowerCase();

    return originalList.filter(
      (item) =>
        item.productName.toLowerCase().includes(q) ||
        item.productCode.toLowerCase().includes(q) ||
        item.currencyCode.toLowerCase().includes(q) ||
        item.issuer.toLowerCase().includes(q) ||
        item.productType.toLowerCase().includes(q),
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  // ===================== CSV EXPORT =====================
  const getCsvListData = () =>
    Promise.resolve(
      originalList.map((item) => ({
        productName: item.productName,
        productCode: item.productCode,
        currencyCode: item.currencyCode,
        productType: item.productType,
      })),
    );

  useEffect(() => {
    BOM.HasPermission("READ_FINANCIAL_INSTRUMENT") &&
      getSecurityInstrumentList();
    BOM.HasPermission("CREATE_FINANCIAL_INSTRUMENT") &&
      rightAction(cnt.RIGHT_ADD_BTN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ===================== ROW SELECTION =====================
  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    // When no row OR more than one row is selected
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);

      // Clear all right buttons first
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });

      // Only enable ADD if user has permission
      if (BOM.HasPermission("CREATE_FINANCIAL_INSTRUMENT")) {
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      }

      return;
    }

    // Exactly ONE row selected
    setSelectedRow(selectedRows);

    // Enable VIEW if permitted
    if (BOM.HasPermission("READ_SECURITY_PRODUCT")) {
      dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    }

    // Enable EDIT if permitted
    if (BOM.HasPermission("UPDATE_SECURITY_PRODUCT")) {
      dispatch({ type: cnt.RIGHT_EDIT_BTN });
    }
  };

  // ===================== ACTIONS =====================
  const handleOnView = () => {
    if (rightBtn?.detailBtn === "primary") {
      navigate("/view-security-product", { state: selectedRow[0]?.id });
    }
  };

  const handleOnCreate = () => {
    if (rightBtn?.addBtn === "primary") {
      navigate("/add-security-product");
    }
  };

  const handleOnEdit = () => {
    if (rightBtn?.editBtn === "primary") {
      navigate("/edit-security-product", { state: selectedRow[0]?.id });
    }
  };

  const itemRender = (_, type, el) => {
    if (type === "prev" || type === "next") return;
    return el;
  };

  const getSecurityInstrumentList = (type = productTypeFilter) => {
    setLoading(true);

    const body = { session };

    let url = apiConfig.GetSecurityProducts; // /fixedincomeproducts

    if (type && type !== "ALL") {
      url = `${url}?productType=${type}`;
    }

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load instruments",
        );
        setLoading(false);
        return;
      }

      const mapped = (res || []).map((el) => ({
        key: el.id,
        id: el.id,
        productName: el.productName || "-",
        currencyCode: el.currencyCode || "-",
        productCode: el.productCode || "-",
        productType: el.productType?.text || "-",
        issuer: el.issuer?.name || "-",
        raw: el,
      }));

      setOriginalList(mapped);
      setSelectedRow([]);
      setCurrentPage(1);
      setLoading(false);
    });
  };

  const handleFilterByProductType = (value) => {
    setProductTypeFilter(value);
    dispatch({ type: cnt.CLEAR_RIGHT_BTN });
    dispatch({ type: cnt.RIGHT_ADD_BTN });
    getSecurityInstrumentList(value);
  };

  return (
    <MainLayout
      isEnableRightSidebar
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.manageSecurityProduct}
    >
      <TopContent
        pageTitle={labelConfig.securityProduct}
        createPageTitle={labelConfig.securityProductListing}
      />

      <Card
        style={{ border: "1px solid #EAECF0", borderRadius: 20, marginTop: 12 }}
      >
        <Spin spinning={loading}>
          <Row
            style={{
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Form form={form}>
              <Space size={12}>
                <Input
                  value={searchTerm}
                  onChange={handleSearchInTable}
                  placeholder="Search by Name"
                  prefix={<SearchLg width={18} color="#667085" />}
                  style={{ width: 350, borderRadius: 10 }}
                />

                <Select
                  defaultValue="ALL"
                  options={productTypeOptions}
                  onSelect={handleFilterByProductType}
                  bordered={false}
                  style={{
                    backgroundColor: "#F9FAFB",
                    border: "1px solid #EAECF0",
                    borderRadius: 50,
                    height: 36,
                    minWidth: 180,
                  }}
                  dropdownStyle={{
                    borderRadius: 8,
                    width: 220,
                  }}
                />
              </Space>
            </Form>

            <DownloadTableButtons
              downloadData={originalList}
              columns={columns}
              fileName="Security Product List"
              onExportClick={getCsvListData}
            />
          </Row>

          <Table
            rowSelection={{
              type: "checkbox",
              hideSelectAll: true,
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <Button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            >
              <ArrowLeft /> Previous
            </Button>

            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              showSizeChanger={false}
              itemRender={itemRender}
              onChange={setCurrentPage}
            />

            <Button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            >
              Next <ArrowRight />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
});

export default connect(mapStateToProps, { rightAction })(ManageSecurityProduct);
